import React, { useState } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { TransactionType } from '@Encore-FastStartup-Phase-Out/encore-shared';
import { observer } from 'mobx-react-lite';

import { sanitizeAmount } from '../../utils/string';
import { Colors } from '../../theme/colors';
import { useStore } from '../../hooks/useStore';

interface EditCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditCreditModal = ({ isOpen, onClose }: EditCreditModalProps) => {
  const {
    userStore: { updateUser, displayedUser },
    orderStore: { createAdminTransaction },
  } = useStore();
  const toast = useToast();
  const bgColor = useColorModeValue(
    Colors.light.background,
    Colors.dark.background,
  );

  const [amount, setAmount] = useState('');
  const [shouldShowAmountError, setShouldShowAmountError] = useState(false);
  const [loading, setLoading] = useState(false);

  const onConfirmRefund = async (amount: number) => {
    setLoading(true);
    try {
      await updateUser(displayedUser.id, {
        credits: amount,
      });

      await createAdminTransaction(amount, TransactionType.AdminCreditChange);
      toast({
        title: 'Success',
        description: 'Credit Balance updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'Failed to update credit balance',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const isAmountValid = () => {
    const value = parseFloat(amount);
    return /^\d+(\.\d{1,2})?$/.test(amount) && !isNaN(value) && value >= 0;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeAmount(e.target.value);
    setAmount(sanitized);
  };

  const handleAmountBlur = () => {
    setShouldShowAmountError(!isAmountValid());
  };

  const handleConfirm = async () => {
    if (isAmountValid()) {
      await onConfirmRefund(parseFloat(amount));
      handleClose();
    } else {
      setShouldShowAmountError(!isAmountValid());
    }
  };

  const handleClose = () => {
    onClose();
    setAmount('');
    setShouldShowAmountError(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size={'lg'}>
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>Edit Credit Balance</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isInvalid={shouldShowAmountError} isRequired mb={4}>
            <FormLabel>Credit Amount (can't be under $0.00)</FormLabel>
            <Input
              placeholder="Enter amount"
              value={amount}
              onChange={handleAmountChange}
              onBlur={handleAmountBlur}
              type={'number'}
              step="0.01"
            />
            <FormErrorMessage>Please enter a valid amount</FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button onClick={handleClose} variant={'outline'}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={handleConfirm}
            variant={'solid'}
            ml={3}
            isDisabled={!isAmountValid() || loading}
            isLoading={loading}
          >
            Confirm Credit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(EditCreditModal);
